import React from 'react';
import { Link } from 'react-router-dom';
import './Welcome.css';

const FEATURES = [
  {
    id: 'journal-stats',
    title: 'See your journal performance at a glance',
    image: '/welcome/01-journal-stats.png',
    text:
      'Track completed positions, journal entries, net P&L, and win rate in one clean dashboard. Instantly spot which behaviors and setups contribute most to your results.',
    imageFirst: true
  },
  {
    id: 'tag-leaderboard',
    title: 'Rank every strategy and behavior with tag leaderboards',
    image: '/welcome/02-tag-leaderboard.png',
    text:
      'Compare trade types, psychology tags, and setups side by side. Find your highest-conviction patterns and remove low-quality habits.',
    imageFirst: false
  },
  {
    id: 'trading-performance',
    title: 'Measure win-loss distribution with clarity',
    image: '/welcome/03-trading-performance.png',
    text:
      'Know exactly how often you win, lose, or break even. Get quick confidence metrics before diving deeper into trade-level analysis.',
    imageFirst: true
  },
  {
    id: 'time-based-performance',
    title: 'Discover your best trading days',
    image: '/welcome/04-time-based-performance.png',
    text:
      'Identify which weekdays consistently perform and which ones hurt your edge. Build routines around your strongest market windows.',
    imageFirst: false
  },
  {
    id: 'date-of-month',
    title: 'Track calendar-day edge across months',
    image: '/welcome/05-date-of-month-pnl.png',
    text:
      'See if specific dates repeatedly generate gains or losses. Use date-based behavior to improve timing and risk control.',
    imageFirst: true
  },
  {
    id: 'auto-insights',
    title: 'Get instant trade insights',
    image: '/welcome/06-auto-insights.png',
    text:
      'Receive actionable observations on streaks, overtrading, and day-wise performance so you can correct mistakes faster.',
    imageFirst: false
  },
  {
    id: 'weekday-chart',
    title: 'Visualize performance with clean, actionable charts',
    image: '/welcome/07-weekday-chart.png',
    text:
      'Turn raw trade logs into visual patterns. Quickly compare weekday outcomes and make data-backed adjustments.',
    imageFirst: true
  },
  {
    id: 'daily-calendar',
    title: 'Review every trading day on a P&L calendar',
    image: '/welcome/08-daily-pnl-calendar.png',
    text:
      'Use a color-coded monthly view to spot winning streaks, drawdowns, and consistency trends at a glance.',
    imageFirst: false
  },
  {
    id: 'pnl-summary',
    title: 'Get a complete P&L summary in seconds',
    image: '/welcome/09-pnl-summary.png',
    text:
      'Monitor your core performance metrics from one summary panel: net profitability, risk efficiency, and trading quality.',
    imageFirst: true
  },
  {
    id: 'cumulative-pnl',
    title: 'Follow your equity curve over time',
    image: '/welcome/10-cumulative-pnl.png',
    text:
      'Track how your P&L compounds across weeks and months. Detect momentum shifts before they become major drawdowns.',
    imageFirst: false
  },
  {
    id: 'manual-entry',
    title: 'Log trades manually with structured inputs',
    image: '/welcome/11-manual-trade-entry.png',
    text:
      'Add trades quickly using guided fields for symbol, quantity, prices, dates, and notes without spreadsheet clutter.',
    imageFirst: true
  },
  {
    id: 'trade-journal',
    title: 'Capture context with smart trade journaling',
    image: '/welcome/12-trade-journal.png',
    text:
      'Tag each trade with source, setup, psychology, and execution behavior. Build a reusable database of what really works for you.',
    imageFirst: false
  },
  {
    id: 'upload',
    title: 'Import broker trade files in minutes',
    image: '/welcome/13-upload-file-v2.png',
    text:
      'Upload your trade book file (.xlsx, .xls, or .csv format). Select your broker to ensure proper parsing.',
    imageFirst: true
  }
];

function Welcome() {
  return (
    <div className="welcome-page">
      <header className="welcome-header">
        <div className="welcome-header-inner">
          <Link to="/" className="welcome-brand">
            <img src="/tradelensy-logo.png" alt="" className="welcome-brand-logo" width={48} height={48} />
            <span className="welcome-brand-name">Tradelensy</span>
          </Link>
          <nav className="welcome-nav" aria-label="Main">
            <a href="#features" className="welcome-nav-link">
              Features
            </a>
            <a href="#about-seo" className="welcome-nav-link">
              About
            </a>
            <Link to="/login" className="welcome-nav-link">
              Log in
            </Link>
            <Link to="/register" className="btn welcome-btn-primary">
              Sign up
            </Link>
          </nav>
        </div>
      </header>

      <section className="welcome-hero">
        <div className="welcome-hero-inner">
          <h1 className="welcome-hero-title">Your trading journal, clearer than ever</h1>
          <p className="welcome-hero-sub">
            Explore every important screen in Tradelensy with large, high-visibility previews. From journal stats to upload workflows, every section shows exactly how you can review performance and improve execution.
          </p>
          <div className="welcome-hero-cta">
            <Link to="/register" className="btn welcome-btn-primary welcome-btn-large">
              Get started free
            </Link>
            <Link to="/login" className="btn welcome-btn-secondary welcome-btn-large">
              Log in
            </Link>
          </div>
        </div>
      </section>

      <section className="welcome-demo" aria-labelledby="demo-heading">
        <div className="welcome-demo-inner">
          <h2 id="demo-heading">Watch the product demo</h2>
          <p className="welcome-demo-lead">
            Get a quick walkthrough of Tradelensy features before exploring each screen in detail.
          </p>
          <div className="welcome-demo-frame-wrap">
            <iframe
              className="welcome-demo-frame"
              src="https://www.youtube.com/embed/1_TKFboCg1M"
              title="Tradelensy product demo"
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>
          <p className="welcome-demo-fallback">
            Prefer YouTube directly?{' '}
            <a
              href="https://www.youtube.com/watch?v=1_TKFboCg1M"
              target="_blank"
              rel="noopener noreferrer"
            >
              Open the demo video
            </a>
            .
          </p>
        </div>
      </section>

      <section id="features" className="welcome-features" aria-labelledby="features-heading">
        <div className="welcome-section-head">
          <h2 id="features-heading">See what you can do</h2>
          <p className="welcome-section-lead">
            Here’s a tour of the app—each screen is built to save you time and make your edge visible.
          </p>
        </div>

        {FEATURES.map((f) => (
          <article key={f.id} id={f.id} className={`welcome-feature ${f.imageFirst ? 'image-left' : 'image-right'}`}>
            <div className="welcome-feature-image-wrap">
              <img src={f.image} alt="" className="welcome-feature-image" loading="lazy" />
            </div>
            <div className="welcome-feature-copy">
              <h3>{f.title}</h3>
              <p>{f.text}</p>
            </div>
          </article>
        ))}
      </section>

      <section id="about-seo" className="welcome-seo" aria-labelledby="seo-heading">
        <div className="welcome-seo-inner">
          <h2 id="seo-heading">Tradelensy — trading journal and analytics for Indian markets</h2>
          <p>
            Tradelensy is a web-based trading journal and performance tool for traders who want organized trade books, clear P&amp;L summaries, and optional structured notes on every position. Whether you enter trades manually in the Trade Wizard or import broker files (CSV / Excel) with support for major Indian brokers, you get a consistent view of completed trades, open positions, and how your results evolve over time.
          </p>
          <p>
            Use daily and cumulative P&amp;L views to understand streaks and drawdowns, then go deeper with journal stats and tag leaderboards that tie your setups, psychology, and trade sources to outcomes. Tradelensy is designed for serious journaling without spreadsheet fatigue—so you can focus on improving process, not fighting formatting.
          </p>
          <p>
            <Link to="/register">Create an account</Link> to start a trade book, or <Link to="/login">log in</Link> if you already use Tradelensy.
          </p>
        </div>
      </section>

      <footer className="welcome-footer">
        <p>© {new Date().getFullYear()} Tradelensy. All rights reserved.</p>
        <p className="welcome-footer-links">
          <Link to="/login">Log in</Link>
          <span aria-hidden="true"> · </span>
          <Link to="/register">Sign up</Link>
        </p>
      </footer>
    </div>
  );
}

export default Welcome;
